/**
 * 오프라인 NFC 태깅 큐 — apps/client 에서 이식 (명세 §16-D11·D12).
 *
 * 인터넷이 끊긴 상태에서 태깅하면 IndexedDB 에 담아두고 온라인 복귀 시 보낸다.
 * clientId(UUID) 가 서버 Attendance.clientId @unique 와 매칭돼 중복 INSERT 를 막는다.
 *
 * 네이티브 변경 0건 — WebView 안에서 IndexedDB 가 그대로 동작한다.
 */
import { openDB, type IDBPDatabase } from 'idb'

/*
 * 🔴 이름을 갈랐다 (2026-09-04). 전에는 `biznomi-offline` 으로 **apps/client 와
 *   완전히 같았다** — 이름·버전·스토어 이름·레코드 필드까지.
 *
 *   경로 분리로 두 세계가 같은 origin 을 쓰게 되면서, 같은 이름이면 **NFC 태깅 큐를
 *   공유한다.** 한쪽이 쌓아 둔 태깅을 다른 쪽이 꺼내 자기 서버로 보낸다.
 *   모양이 같아서 예외도 안 나고 조용히 어긋난다.
 *   태깅 시각 기록이 이 제품의 본질이라, 여기가 제일 아픈 자리다.
 *
 * ⚠ 이름을 바꾸면 옛 DB(`biznomi-offline`)에 남아 있던 큐는 **따라오지 않는다.**
 *   표준 제품은 아직 실기기 태깅을 한 적이 없어 비어 있을 것으로 보고 바꿨다.
 *   확인은 브라우저에서만 되므로 여기서 못 했다 — 나중에 또 바꿀 일이 있으면
 *   `indexedDB.databases().then(console.log)` 로 **먼저 보고** 바꿀 것.
 */
const DB_NAME = 'biznomi-offline-mtt'
/*
 * v2 (2026-09-08) — 레코드에 `orgCode` 를 더했다. 스토어 구조는 그대로라
 * upgrade 에서 할 일이 없다. **옛 레코드는 orgCode 가 undefined 로 남는다** —
 * 그건 «어느 회사 것인지 모른다» 는 뜻이고, sync 가 그걸 버리지 않고 보낸다
 * (아래 canSendTag 주석 참조).
 */
const DB_VERSION = 2
const STORE = 'pendingAttendance'

export interface QueuedTag {
  /** 멱등 키 — 큐 PK 이자 서버 중복 차단 키 */
  clientId: string
  nfcUid: string
  requestType: 'CHECKIN' | 'CHECKOUT'
  nfcSignature?: string
  /** 태그를 실제로 찍은 시각. 나중에 전송돼도 이 시각이 기록된다 */
  clientTimestamp: string
  /** 큐에 들어온 시각 — 전송 순서 정렬용 */
  queuedAt: string
  /**
   * 🔴 찍을 때 로그인해 있던 회사 코드 (2026-09-08).
   *
   * 없으면 **큐가 어느 회사 것인지 모른다.** 같은 브라우저에서 다른 회사로
   * 로그인하면 sync 가 이 태깅을 그 회사 토큰으로 보내고, 서버가 403
   * «다른 조직의 자원에 접근할 수 없습니다» 를 낸다. 403 은 4xx 라 sync 가
   * **영구 실패로 보고 큐에서 뺀다** — 실제로 찍은 시각이 사라진다.
   * 화면에 뜨기는 하지만(addDropped) 되찾을 수는 없다.
   *
   * 태깅 시각을 잃지 않는 게 이 제품의 제1원칙이라, 여기가 제일 아픈 자리다.
   *
   * ⚠ v1 레코드에는 이 칸이 없다(undefined). 그건 "다른 회사 것" 이 아니라
   *   "모른다" 이므로 막지 않는다.
   */
  orgCode?: string
}

/**
 * 지금 로그인한 회사로 보내도 되는 태깅인가.
 *
 * 모르면(v1 레코드) 보낸다. 확실히 다를 때만 막는다 —
 * **버리는 것보다 미루는 게 낫다.** 원래 회사로 다시 로그인하면 그때 나간다.
 */
export function canSendTag(tag: QueuedTag, currentOrgCode: string): boolean {
  if (!tag.orgCode) return true
  return tag.orgCode === currentOrgCode
}

let dbPromise: Promise<IDBPDatabase> | null = null

function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'clientId' })
        }
      },
    })
  }
  return dbPromise
}

export async function enqueueTag(tag: QueuedTag): Promise<void> {
  const db = await getDB()
  await db.put(STORE, tag)
}

/** 찍은 순서대로 (queuedAt 오름차순) */
export async function getQueuedTags(): Promise<QueuedTag[]> {
  const db = await getDB()
  const all = (await db.getAll(STORE)) as QueuedTag[]
  return all.sort((a, b) => a.queuedAt.localeCompare(b.queuedAt))
}

export async function dequeueTag(clientId: string): Promise<void> {
  const db = await getDB()
  await db.delete(STORE, clientId)
}

export async function queuedCount(): Promise<number> {
  const db = await getDB()
  return db.count(STORE)
}
